import { Mixpanel, MxEvent } from './Mixpanel';
import ThumbnailCardUI, { ACTION } from './ThumbnailCardUI';
import { checkIfMemberHaveEditorAccess, useUserContext } from '../contexts/UserContext';

import { Link } from 'react-router-dom';
import { OPEN_PROJECT_ID } from '../config/constant/curatorConstants';
import ThumbnailCardV2 from './ThumbnailCardV2';
import { dispatcher } from '../helpers/projectHelper';
import { getCuratorProjectPath } from '../helpers/urlHelper';
import {
  getCurrentlyOpenProjectId,
  getDownloadAndCollect3DFileAction,
  windowOpen,
} from '../helpers/jsHelper';
import { icons } from '../assets';
import { myprojectsActions } from '../redux/slicers/my-projects/myProjectsSlicer';
import { useProjectActions } from '../hooks/useProjectActions';
import { useCheckCuratorActive } from '../hooks/useCuratorActive';
import { errorToast } from '../helpers/toastHelper';
import { PERMISSION } from '../constants/permissionConstants';
import { useStaticPermissions } from '../hooks/usePermissions';
import { FAST_PREVIEW_TAG } from '../constants/constants';

const { InfoIcon } = icons;

export const ThumbnailCardProject = ({
  data,
  size,
  fullWidth,
  onSelect,
  selected,
  hideFavourite,
  onEdit,
  onDelete,
  customActions,
  designV2,
  showMenu,
  redirect,
  ...rest
}) => {
  const {
    onDelete: _onDelete,
    onFavouriteToggle,
    favouriteLoading,
    downloadProject,
    downloadLoading,
    onShare,
    addToCollection,
    requestTo3DFilesCollection,
    download3DFilesZip,
  } = useProjectActions({
    item: data,
  });

  const { isGuestUser, canDownload3DFile } = useUserContext();
  const { isCuratorActive, getCuratorList } = useCheckCuratorActive();
  const {hasPermission} = useStaticPermissions()

  const isActiveCurator = async () => {
    const curatorActive = await isCuratorActive();
    return curatorActive;
  };

  let combinedCustomActions = {
    [ACTION.EDIT]: { label: 'Info', icon: designV2 ? InfoIcon : <InfoIcon /> },
    ...customActions,
  };

  if (canDownload3DFile) {
    // generating download & collect 3d files
    combinedCustomActions = getDownloadAndCollect3DFileAction({
      actions: combinedCustomActions,
      data,
      rest,
      designV2,
      download3DFilesZip,
      requestTo3DFilesCollection,
      onUpdate: (updatedData) => dispatcher(myprojectsActions.update(updatedData)),
    });
  }

  combinedCustomActions[ACTION.DOWNLOAD] = false;
  const handleDelete = async (id, data, originalData) => {
    const openCurator = await isActiveCurator();
    const isActive =
      openCurator &&
      getCurrentlyOpenProjectId(
        getCuratorList()?.map((url) => url?.uniqueUrl),
        data?.id
      );
    if (isActive) {
      errorToast('Please close or save this project before deleting.');
    } else {
      onDelete?.(id, data, originalData) || _onDelete?.(id, data, originalData);
    }
  };

  let curatorProjectId = data.id;
  const autosaveId = data.is_autosave ? data.id : null;
  if (data.is_autosave) {
    curatorProjectId = data.parent || OPEN_PROJECT_ID;

    combinedCustomActions[ACTION.ADD_TO_COLLECTION] = false;
    combinedCustomActions[ACTION.DOWNLOAD] = false;
    combinedCustomActions[ACTION.EDIT] = false;
    combinedCustomActions[ACTION.SHARE] = false;
    combinedCustomActions[ACTION.COLLECT_3D_FILES] = false;
    combinedCustomActions[ACTION.DOWNLOAD_3D_FILES] = false;
  }

  const url = getCuratorProjectPath(curatorProjectId, autosaveId);

  const handleMixPanelOpenProject = (Project_opened_from) => {
    // console.log("mx: ",data);

    Mixpanel.track(true, MxEvent.EDIT_IN_3D_PROJECT, {
      project_id: data?.id,
      project_name: data?.name,
    });

    Mixpanel.track(true, MxEvent.CURATOR_STARTED, {
      'Curator project type': 'project',
      'Curator opened from': Project_opened_from,
      'Project id': data?.id,
      'Project name': data?.name,
      'Project isFavourite': data?.is_favourite,
      'Project creator id': data?.member_details?.id,
      'Project creator name': data?.member_details?.name,
      'Project creator email': data?.member_details?.email,
      'Project created at': data?.created_at,
    });
  };

  const titleProps = !redirect
    ? null
    : {
        as: Link,
        target: '_blank',
        to: url,
        onClick: (e) => {
          if (!checkIfMemberHaveEditorAccess()) {
            e.preventDefault();
            return;
          }
          handleMixPanelOpenProject('Title of project thumbnail');
        },
      };

  const onCardClick = () => {
    if (!redirect) return;

    handleMixPanelOpenProject('Title of project thumbnail');
    windowOpen(url, rest?.redirectTarget || '_blank');
  };

  const handleEditWithFastPreview = () => {
    if (!checkIfMemberHaveEditorAccess()) return;

    let _url = url + `&${FAST_PREVIEW_TAG}=true`

    handleMixPanelOpenProject('Edit with  fast preview button on template thumbnail');
    window.open(_url, '_blank');
  };

  return designV2 ? (
    <ThumbnailCardV2
      onFavouriteChange={onFavouriteToggle(data)}
      onSelect={onSelect}
      selected={selected}
      isFavourite={data.is_favourite}
      hideFavourite={hideFavourite}
      favouriteLoading={favouriteLoading}
      titleProps={isGuestUser() ? null : titleProps}
      data={data}
      name={data?.name}
      thumbnail={data?.thumbnail}
      date={data.updated_at}
      userCard
      showMenu={showMenu && !isGuestUser()}
      to={isGuestUser() || !redirect ? null : url}
      id={data?.id}
      // redirect={redirect}
      onCardClick={onCardClick}
      target="_blank"
      showCheckbox
      isFavouriteIcon
      onEdit={onEdit}
      onDelete={handleDelete}
      onShare={onShare}
      customActions={combinedCustomActions}
      onDownload={downloadProject}
      downloadLoading={downloadLoading}
      onAddToCollection={addToCollection}
      onEditWithFastPreview={(isGuestUser() || hasPermission(PERMISSION.CAN_USE_FAST_PREVIEW)) ? handleEditWithFastPreview : null}
      {...rest}
    />
  ) : (
    <ThumbnailCardUI
      key={data.id}
      id={data.id}
      onEdit={onEdit}
      to={url}
      target="_blank"
      thumbnail={data?.thumbnail}
      images={data?.images}
      name={data.name}
      isFavourite={data.is_favourite}
      favouriteLoading={favouriteLoading}
      date={data.updated_at}
      onDelete={handleDelete}
      onShare={onShare}
      onFavouriteChange={onFavouriteToggle(data)}
      customActions={combinedCustomActions}
      size={size}
      titleProps={isGuestUser() ? null : titleProps}
      onDownload={downloadProject}
      downloadLoading={downloadLoading}
      fullWidth={fullWidth}
      onSelect={onSelect}
      selected={selected}
      onAddToCollection={addToCollection}
      hideFavourite={hideFavourite}
      data={data}
      onEditWithFastPreview={(isGuestUser() || hasPermission(PERMISSION.CAN_USE_FAST_PREVIEW)) ? handleEditWithFastPreview : null}
      {...rest}
    />
  );
};
